import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadCloud } from 'lucide-react';
import { Button } from '@repo/ui/src/components/ui/button';
import { cn } from '@repo/ui/src/lib/utils';

const ACCEPTED_EXTENSIONS = ['.xlsx', '.csv'];

interface UploadStepProps {
  onAnalyze: (file: File) => void;
  isPending: boolean;
  error: Error | null;
}

function isAcceptedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

export function UploadStep({ onAnalyze, isPending, error }: UploadStepProps) {
  const { t } = useTranslation('studentImport');
  const [isDragging, setIsDragging] = useState(false);
  const [rejected, setRejected] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    if (!isAcceptedFile(file)) {
      setRejected(true);
      return;
    }
    setRejected(false);
    onAnalyze(file);
  };

  return (
    <div className="py-2">
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={cn(
          'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-14 text-center cursor-pointer transition-colors',
          isDragging
            ? 'border-brand-500 bg-brand-500/5'
            : 'border-surface-200 hover:border-surface-300 hover:bg-surface-50',
        )}
      >
        <UploadCloud className={cn('h-9 w-9', isDragging ? 'text-brand-500' : 'text-surface-400')} />
        <div>
          <p className="font-medium text-surface-800">{t('upload.dropHint')}</p>
          <p className="text-sm text-surface-500 mt-1">{t('upload.formats')}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isPending}
          onClick={(e) => {
            e.stopPropagation();
            inputRef.current?.click();
          }}
        >
          {t('upload.browse')}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {isPending && <p className="text-sm text-surface-500 mt-3 text-center">{t('upload.analyzing')}</p>}
      {rejected && <p className="text-sm text-red-600 mt-3 text-center">{t('upload.rejectedFormat')}</p>}
      {error && <p className="text-sm text-red-600 mt-3 text-center">{error.message || t('upload.error')}</p>}
    </div>
  );
}
